'use client';

import { useState } from 'react';
import type { LeadContact, SchoolChoice } from '@/lib/grants-embed/input';
import type { GrantReport } from '@/lib/grants-embed/lookups';
import { GRANTS_EVENT, pushGatedCollateralRequest, pushGrantsEvent } from '@/lib/grants-embed/embed-analytics';
import { ComparisonReveal } from './comparison-reveal';
import { GrantReportView } from './grant-report-view';
import { GrantsGate } from './grants-gate';
import { GrantsPromos } from './grants-promos';
import { ResultsLayout } from './results-layout';
import { SharePanel } from './share-panel';

interface Lead {
  leadId: string;
  shareToken: string;
  report: GrantReport;
}

type Step =
  | { kind: 'gate'; error: string | null; pick?: { school: SchoolChoice; query: string } }
  | { kind: 'revealing'; school: SchoolChoice; lead: Lead | null }
  | { kind: 'results'; lead: Lead };

/**
 * The public grants finder: a gate that takes school/district, name and email together, a short reveal
 * of the matched peers, then the report with FMX's promos alongside. Marketing links straight
 * here; `initialEmail` prefills the gate when the link already knows the visitor. `appOrigin` is
 * the canonical app URL, so the signup link starts sign-in on the host FMX OAuth returns to.
 */
export function GrantsFinder({ utm, initialEmail, appOrigin }: { utm: Record<string, string>; initialEmail?: string; appOrigin?: string }) {
  const [step, setStep] = useState<Step>({ kind: 'gate', error: null });
  // Kept across a failed attempt so the gate comes back prefilled; the email also feeds the signup link.
  const [contact, setContact] = useState<Partial<LeadContact>>({ email: initialEmail });
  const email = contact.email ?? '';

  async function findGrants(entered: LeadContact, school: SchoolChoice, query: string) {
    setContact(entered);
    setStep({ kind: 'revealing', school, lead: null });
    try {
      const res = await fetch('/api/embed/grants/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...entered, schoolQuery: query, school, utm, referrer: document.referrer || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Something went wrong. Please try again.');
      pushGrantsEvent(GRANTS_EVENT.leadCaptured, { matchCount: data.report.matches.length });
      pushGatedCollateralRequest({
        firstName: entered.firstName,
        lastName: entered.lastName,
        company: data.report.districtName || school.districtName,
        email: entered.email,
      });
      setStep({ kind: 'revealing', school, lead: data });
    } catch (err) {
      setStep({ kind: 'gate', error: err instanceof Error ? err.message : 'Something went wrong. Please try again.', pick: { school, query } });
    }
  }

  if (step.kind === 'gate') {
    return <GrantsGate initialContact={contact} initialPick={step.pick} error={step.error} onSubmit={findGrants} />;
  }

  if (step.kind === 'revealing') {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        <ComparisonReveal
          subjectName={step.school.districtName}
          peers={step.lead?.report.peers ?? null}
          onDone={() => step.lead && setStep({ kind: 'results', lead: step.lead })}
        />
      </main>
    );
  }

  const { report, shareToken } = step.lead;
  return (
    <main>
      <ResultsLayout rail={<GrantsPromos email={email || undefined} appOrigin={appOrigin} />}>
        {report.matches.length > 0 ? (
          <div className="space-y-10">
            <GrantReportView report={report} />
            <div className="max-w-xl">
              <SharePanel report={report} shareToken={shareToken} />
            </div>
          </div>
        ) : (
          <div className="max-w-2xl space-y-3">
            <h2 className="g-display text-3xl font-extrabold">No grant matches for {report.districtName} right now.</h2>
            <p className="text-[var(--g-ink-soft)]">
              New federal grants post every month. Create a free account to keep watching your district against its peers.
            </p>
          </div>
        )}
      </ResultsLayout>
    </main>
  );
}
